import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { usePlatformConfig } from "@/lib/platformConfig";

const dismissKey = (uid) => `td_announcement_dismissed_${uid || "anon"}`;

// Site-wide, member-dismissible announcement. Visible on every page below the
// nav, controlled by the super-admin toggle (/admin/platform/settings/announcement).
// Dismissal is remembered per member (keyed by the message copy, so a changed
// announcement resurfaces).
export default function AnnouncementBanner() {
  const { user } = useAuth();
  const { announcement } = usePlatformConfig();
  const navigate = useNavigate();
  const message = announcement?.message || "";
  const linkText = announcement?.link_text || "";
  const linkUrl = announcement?.link_url || "";

  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey(user?.user_id)) === message; } catch { return false; }
  });

  if (!announcement?.enabled || !message || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(user?.user_id), message); } catch { /* ignore */ }
    setDismissed(true);
  };

  const openLink = () => {
    if (linkUrl) window.open(linkUrl, "_blank", "noopener,noreferrer");
    else navigate("/help");
  };

  const idx = linkText ? message.indexOf(linkText) : -1;
  const before = idx >= 0 ? message.slice(0, idx) : message;
  const after = idx >= 0 ? message.slice(idx + linkText.length) : "";

  return (
    <div data-testid="announcement-banner" className="w-full bg-brand text-white">
      <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 sm:px-6 py-2.5">
        <p className="flex-1 text-[13px] sm:text-sm leading-snug font-sans">
          {before}
          {idx >= 0 && (
            <button
              type="button"
              data-testid="announcement-banner-link"
              onClick={openLink}
              className="underline font-semibold hover:opacity-90"
            >
              {linkText}
            </button>
          )}
          {after}
        </p>
        <button
          type="button"
          data-testid="announcement-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="shrink-0 p-1 rounded-md hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
